/**
 * CloudStorageProvider —— StorageProvider 的云端实现（docs/09 §6.2/§6.1）。
 * 保存流程：拆块 → sha256 → POST /blobs/check 去重 → 预签名直传缺失块（文件不过应用服务器）
 * → POST /projects/:id/versions 提交 manifest（乐观锁 baseVersionSeq，冲突返回 409 语义）。
 * 加载流程：读 head manifest → 预签名 GET 直读每个 blob。
 *
 * P1 范围：projects（场景草稿 scene.json 作为项目主体）已打通；
 *   assets（模型二进制入库）提供 put/getBinary 能力，模型库 store 迁移见 P1 后续；
 *   skills / prefs 为 P2 占位。
 */
import type {
  AssetMeta,
  AssetRef,
  KeyValueStore,
  ProjectBundle,
  ProjectSummary,
  SaveOptions,
  SaveResult,
  SkillHistoryEntry,
  SkillManifest,
  SkillSummary,
  StorageProvider,
  VersionSummary,
} from '@f3b/shared'
import {
  ApiError,
  apiBlobCheck,
  apiBlobPresign,
  apiProjectDetail,
  apiSaveVersion,
} from './api'

async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes as BufferSource)
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

function contentTypeOf(name: string): string | undefined {
  if (name.endsWith('.json')) return 'application/json'
  if (name.endsWith('.glb')) return 'model/gltf-binary'
  if (name.endsWith('.hdr')) return 'application/octet-stream'
  if (name.endsWith('.png')) return 'image/png'
  if (name.endsWith('.jpg') || name.endsWith('.jpeg')) return 'image/jpeg'
  return 'application/octet-stream'
}

/** Uint8Array → Blob（TS5.9 类型化数组泛型下 Uint8Array<ArrayBufferLike> 不能直接作 BlobPart，显式收窄） */
function toBlob(bytes: Uint8Array): Blob {
  return new Blob([bytes as unknown as BlobPart])
}

export class CloudStorageProvider implements StorageProvider {
  readonly kind = 'cloud' as const

  private readonly tenantPublicId: string

  constructor(tenantPublicId: string) {
    this.tenantPublicId = tenantPublicId
  }

  projects = {
    list: async (): Promise<ProjectSummary[]> => {
      const { apiListProjects } = await import('./api')
      const rows = await apiListProjects(this.tenantPublicId)
      return rows.map((p) => ({ id: p.publicId, name: p.name, updatedAt: p.updatedAt }))
    },

    load: async (id: string): Promise<ProjectBundle> => {
      const detail = await apiProjectDetail(this.tenantPublicId, id)
      const files: Record<string, Uint8Array> = {}
      for (const [name, sha] of Object.entries(detail.headManifest ?? {})) {
        const { url } = await apiBlobPresign(this.tenantPublicId, sha, 'get')
        if (!url) throw new Error(`预签名下载地址为空：${name}`)
        const res = await fetch(url)
        if (!res.ok) throw new Error(`云端文件下载失败：${name}`)
        files[name] = new Uint8Array(await res.arrayBuffer())
      }
      return { files }
    },

    /** 覆盖式保存（§6.1）：check 去重 → 预签名直传缺失 → 提交 manifest（乐观锁） */
    save: async (id: string, bundle: ProjectBundle, opts: SaveOptions): Promise<SaveResult> => {
      const entries = Object.entries(bundle.files)
      const manifest: Record<string, string> = {}
      const bySha = new Map<string, Uint8Array>()
      const nameBySha = new Map<string, string>()
      for (const [name, bytes] of entries) {
        const sha = await sha256Hex(bytes)
        manifest[name] = sha
        bySha.set(sha, bytes)
        // 两个文件名可能算出同一个 sha（内容相同）：只需要一个代表性文件名做 contentType 猜测，
        // 不能用 bySha.get(sha) === b 做引用比较——重复内容时后一次 set 会换掉 map 里的引用，
        // 导致按引用查找永远只命中最后一个同名 sha 对应的文件，找不到时误判成 'file' 兜底类型。
        if (!nameBySha.has(sha)) nameBySha.set(sha, name)
      }
      const shas = [...bySha.keys()]
      const check = await apiBlobCheck(this.tenantPublicId, shas)
      for (const sha of check.missing) {
        const name = nameBySha.get(sha) ?? 'file'
        const bytes = bySha.get(sha)
        if (!bytes) continue
        const { uploadUrl } = await apiBlobPresign(this.tenantPublicId, sha, 'put', contentTypeOf(name))
        if (!uploadUrl) throw new Error(`预签名上传地址为空：${name}`)
        const res = await fetch(uploadUrl, { method: 'PUT', body: toBlob(bytes) })
        if (!res.ok) throw new Error(`云端上传失败：${name}（HTTP ${res.status}）`)
      }
      const blobSizes: Record<string, number> = {}
      for (const [sha, bytes] of bySha) blobSizes[sha] = bytes.byteLength
      try {
        const r = await apiSaveVersion(
          this.tenantPublicId,
          id,
          manifest,
          opts.baseVersionId !== undefined ? Number(opts.baseVersionId) : undefined,
          opts.label,
          blobSizes,
        )
        return { versionId: String(r.seq), conflict: false }
      } catch (err) {
        if (err instanceof ApiError && err.status === 409) {
          // 冲突：不静默覆盖（§6.1）；headVersionId 用最新 seq，前端弹三选一
          const detail = await apiProjectDetail(this.tenantPublicId, id)
          return { versionId: '', conflict: true, headVersionId: String(detail.versionSeq) }
        }
        throw err
      }
    },

    versions: async (): Promise<VersionSummary[]> => {
      // P1 场景保存只用到 head；版本列表由项目详情页承载
      return []
    },

    restore: async (): Promise<void> => undefined,
  }

  assets = {
    put: async (file: File): Promise<AssetRef> => {
      const bytes = new Uint8Array(await file.arrayBuffer())
      const sha = await sha256Hex(bytes)
      const check = await apiBlobCheck(this.tenantPublicId, [sha])
      if (check.missing.includes(sha)) {
        const { uploadUrl } = await apiBlobPresign(this.tenantPublicId, sha, 'put', file.type)
        if (!uploadUrl) throw new Error('预签名上传地址为空')
        const res = await fetch(uploadUrl, { method: 'PUT', body: toBlob(bytes) })
        if (!res.ok) throw new Error(`模型上传失败（HTTP ${res.status}）`)
      }
      return { sha256: sha, name: file.name, contentType: file.type, sizeBytes: bytes.byteLength }
    },

    getBinary: async (ref: AssetRef): Promise<ArrayBuffer> => {
      const { url } = await apiBlobPresign(this.tenantPublicId, ref.sha256, 'get')
      if (!url) throw new Error('预签名下载地址为空')
      const res = await fetch(url)
      if (!res.ok) throw new Error(`模型下载失败（HTTP ${res.status}）`)
      return res.arrayBuffer()
    },

    /** 模型库清单（docs/10 §2.10-②：服务端 asset 表为唯一数据源） */
    list: async (): Promise<AssetMeta[]> => {
      const { apiListAssets } = await import('./api')
      const rows = await apiListAssets(this.tenantPublicId)
      return rows.map((r) => ({
        ref: {
          sha256: r.blobSha,
          name: r.name,
          contentType: r.contentType ?? '',
          sizeBytes: r.sizeBytes,
        },
        kind: r.kind,
        meta: r.meta,
      }))
    },
  }

  skills = {
    list: async (): Promise<SkillSummary[]> => {
      const { apiSkillList } = await import('./api')
      const rows = await apiSkillList(this.tenantPublicId)
      return rows.map((r) => ({
        id: r.skillKey,
        title: r.title,
        version: String(r.headVersion),
        origin: (r.origin as SkillSummary['origin']) ?? 'user-authored',
        status: (r.status as SkillSummary['status']) ?? 'stable',
        missingTools: [],
      }))
    },
    get: async (id: string): Promise<SkillManifest> => {
      const { apiSkillGet } = await import('./api')
      const detail = await apiSkillGet(this.tenantPublicId, id)
      return detail.manifest as unknown as SkillManifest
    },
    upsert: async (m: SkillManifest): Promise<void> => {
      const { apiSkillList, apiSkillUpsert } = await import('./api')
      const rows = await apiSkillList(this.tenantPublicId)
      const exists = rows.some((r) => r.skillKey === m.id)
      await apiSkillUpsert(this.tenantPublicId, m, exists ? 'patch' : 'create')
    },
    history: async (id: string): Promise<SkillHistoryEntry[]> => {
      const { apiSkillGet } = await import('./api')
      const detail = await apiSkillGet(this.tenantPublicId, id)
      return detail.versions.map((v) => ({
        version: String(v.version),
        manifest: v.manifest as unknown as SkillManifest,
        atMs: new Date(v.createdAt).getTime(),
      }))
    },
  }

  prefs: KeyValueStore = {
    get: async <T>(key: string): Promise<T | undefined> => {
      const { apiGetPrefs } = await import('./api')
      const all = await apiGetPrefs()
      return (all[key] as T | undefined)
    },
    set: async <T>(key: string, value: T): Promise<void> => {
      const { apiPatchPrefs } = await import('./api')
      await apiPatchPrefs({ [key]: value })
    },
    remove: async (key: string): Promise<void> => {
      const { apiPatchPrefs } = await import('./api')
      await apiPatchPrefs({ [key]: null })
    },
  }
}
